import React from 'react';
import { useParams } from 'react-router';
import { Route } from '@reverbdotcom/commons/src/route_settings';
import { ADS_ADMIN_ADVERTISERS_ROOT, ADS_ADMIN_AD_ZONES_ROOT, AD_PREVIEWS_ROOT } from '../components/shared/url_helpers';

// v7 child routes must use relative paths (the v3 tree allowed absolute ones)
const relative = (path: string) => path.replace(/^\//, '');

const advertisersRoot = relative(ADS_ADMIN_ADVERTISERS_ROOT);
const adZonesRoot = relative(ADS_ADMIN_AD_ZONES_ROOT);
const adPreviewsRoot = relative(AD_PREVIEWS_ROOT);

const advertiserEditPath = `${advertisersRoot}/:advertiserUuid/edit`;
const adZoneEditPath = `${adZonesRoot}/:adZoneUuid/edit`;
const adCampaignsPath = `${advertisersRoot}/:advertiserUuid/ad_campaigns`;
const adCampaignEditPath = `${adCampaignsPath}/:adCampaignUuid/edit`;
const adGroupsPath = `${adCampaignsPath}/:adCampaignUuid/ad_groups`;
const adGroupEditPath = `${adGroupsPath}/:adGroupUuid/edit`;
const adsPath = `${adGroupsPath}/:adGroupUuid/ads`;
const adEditPath = `${adsPath}/:adUuid/edit`;

function lazyDefault(load: () => Promise<{ default: React.ComponentType }>) {
  return async () => ({ Component: (await load()).default });
}

// These containers take their route params as named props (the v3
// getComponent wrappers mapped them from the router-injected props.params).
function lazyWithRouteParams(
  load: () => Promise<{ default: React.ComponentType<any> }>,
  paramNames: string[],
) {
  return async () => {
    const Component = (await load()).default;

    return {
      Component: function AdsAdminRoutePage() {
        const params = useParams();
        const paramProps = Object.fromEntries(paramNames.map((name) => [name, params[name]]));

        return <Component {...paramProps} />;
      },
    };
  };
}

export const AdsAdminRoutes = [
  <Route
    key={ADS_ADMIN_AD_ZONES_ROOT}
    path={adZonesRoot}
    handle={{ clientSide: true }}
    lazy={lazyDefault(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_zones_index_container'
    ))}
  />,
  <Route
    key={AD_PREVIEWS_ROOT}
    path={adPreviewsRoot}
    handle={{ clientSide: true }}
    lazy={lazyDefault(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_previews_index'
    ))}
  />,
  <Route
    key={adZoneEditPath}
    path={adZoneEditPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_zone_form_container'
    ), ['adZoneUuid'])}
  />,
  <Route
    key={ADS_ADMIN_ADVERTISERS_ROOT}
    path={advertisersRoot}
    handle={{ clientSide: true }}
    lazy={lazyDefault(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/advertisers_index_container'
    ))}
  />,
  <Route
    key={advertiserEditPath}
    path={advertiserEditPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/advertiser_form_container'
    ), ['advertiserUuid'])}
  />,
  <Route
    key={adCampaignsPath}
    path={adCampaignsPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_campaigns_index_container'
    ), ['advertiserUuid'])}
  />,
  <Route
    key={adGroupsPath}
    path={adGroupsPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_groups_index_container'
    ), ['adCampaignUuid', 'advertiserUuid'])}
  />,
  <Route
    key={adGroupEditPath}
    path={adGroupEditPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_group_form_container'
    ), ['advertiserUuid', 'adCampaignUuid', 'adGroupUuid'])}
  />,
  <Route
    key={adsPath}
    path={adsPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ads_index_container'
    ), ['advertiserUuid', 'adCampaignUuid', 'adGroupUuid'])}
  />,
  <Route
    key={adCampaignEditPath}
    path={adCampaignEditPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_campaign_form_container'
    ), ['advertiserUuid', 'adCampaignUuid'])}
  />,
  <Route
    key={adEditPath}
    path={adEditPath}
    handle={{ clientSide: true }}
    lazy={lazyWithRouteParams(() => import(
      /* webpackChunkName: 'ads_admin' */
      '../components/ad_admin/ad_form_container'
    ), ['advertiserUuid', 'adCampaignUuid', 'adGroupUuid', 'adUuid'])}
  />,
];
